/**
 * ColorSelector Component
 *
 * Provides a dropdown interface for selecting measurement line colors.
 * Includes predefined color options with both hex values and friendly names.
 */

import React from "react";

import {
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  SxProps,
  Theme,
} from "@mui/material";

interface ColorSelectorProps {
  /** The currently selected color value (hex string) */
  selectedColor: string;
  /** Callback function triggered when a color is selected */
  onSelectColor: (event: SelectChangeEvent<string>) => void;
  /** Whether to show the input label above the selector */
  showLabel?: boolean;
  /** Whether to show color names alongside color swatches */
  showNames?: boolean;
  /** Name attribute for the form control */
  name?: string;
  /** Minimum width of the selector in pixels */
  minWidth?: number;
  /** Custom styles to apply to the Select component */
  selectSX?: SxProps<Theme>;
  /** Additional props to pass to the Select's MenuProps */
  menuProps?: any;
}

/**
 * Predefined color palette with hex values mapped to friendly names
 * Used for measurement line colors in the model viewer
 */
export const colorDictionary: { [id: string]: string } = {
  ["#dc2626"]: "red",
  ["#ea580c"]: "orange",
  ["#eab308"]: "yellow",
  ["#16a34a"]: "green",
  ["#2563eb"]: "blue",
  ["#9333ea"]: "purple",
  ["#e11d48"]: "pink",
  ["#1f2937"]: "black",
};

/**
 * Utility function to validate and find a color in the color dictionary
 * @param color - The color hex string to search for
 * @returns The validated color if found, otherwise returns the first color in the dictionary
 */
export const findColor = (color: string): string => {
  // Check if the provided color exists in our dictionary
  if (colorDictionary[color]) {
    return color;
  }
  // Fallback to the first available color if not found
  return Object.keys(colorDictionary)[0];
};

const ColorSelector: React.FC<ColorSelectorProps> = ({
  selectedColor,
  onSelectColor,
  showLabel = true,
  showNames = true,
  name = "color",
  minWidth = 160,
  selectSX,
  menuProps,
}) => {
  return (
    <Card
      sx={{
        backgroundColor: "lightgray",
        
      }}
    >
      <FormControl
        fullWidth={showLabel}
        aria-labelledby={!showLabel ? undefined : `${name}-select-label`}
      >
        {/* Conditional label display */}
        {showLabel && (
          <InputLabel id={`${name}-select-label`} color="primary">
            Color
          </InputLabel>
        )}

        <Select
          fullWidth={showLabel}
          name={name}
          label={!showLabel ? undefined : name}
          id={`${name}-select`}
          aria-labelledby={`${name}-select-label`}
          labelId={`${name}-select-label`}
          value={selectedColor}
          onChange={onSelectColor}
          MenuProps={{
            ...menuProps,
          }}
          sx={{
            minWidth: showNames ? `${minWidth}px` : undefined,
            height: "43.42px",
            ...selectSX,
          }}
        >
          {Object.entries(colorDictionary).map(([hexColor, colorName]) => {
            return (
              <MenuItem key={hexColor} value={hexColor} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ width: '100%' }}
                >
                  <div
                    key={hexColor}
                    style={{
                      backgroundColor: hexColor,
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      flexShrink: 0,
                      marginLeft: "auto",
                      marginRight: "auto",
                    }}
                    aria-label={`${colorName} color`}
                  />
                </Stack>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Card>
  );
};

export default ColorSelector;
